import React, {useEffect, useMemo, useState} from 'react';
import {FieldInputProps} from 'formik';
import {Box, Button, Grid, Typography} from '@mui/material';
import {useQuery} from '@apollo/client';
import {toString, merge} from 'lodash';
import {recommendFieldsType} from '../CreateRecommend/recommendFields';
import {
  criticalIllnessCoverField,
  deferredPeriodField,
  protectionInTrustField,
  investmentLinkedField,
  liveCoverField,
  livesAssuredField,
  monthlyIncomeTextField,
  premiumTypeField,
  protectionFields,
  protectionPlanNotesField,
  protectionTypeField,
  protectionTypeToFieldsMatrix,
  setUpProtectionFields,
  termField,
  termTypeField,
  toAgeField,
  waiverOfPremiumField,
} from './protectionFields';
import AutoField from '../../../components/AutoForm/AutoFieldNew';
import RecommendPlanForm from '../CreateRecommend/RecommendSourceDestination/RecommendPlanForm';
import OWNERSHIP from '../../../constants/Ownership';
import {RecommendProps} from '../CreateRecommend/RecommendSourceDestination/validators';
import {useRecommendContext} from '../CreateRecommend/CreateRecommendHelpers';
import {OV_PROTECTION_RECOMMENDATION_DETAILS} from '../RecommendsGrid/ovPotQueries';
import {
  OvProtectionrecommendDetails,
  OvProtectionrecommendDetailsVariables,
  OvProtectionrecommendDetails_ovrecommendDetails,
} from '../../../schema-types/OvProtectionrecommendDetails';
import {ProtectionTypeEnum} from '../../../schema-types/globalTypes';
import Loading from '../../../components/Loading/Loading';
import {
  useAutoForm,
  useAutoField,
  AutoFieldHelpers,
  SetValuesWithSourceKey,
} from '../../../components/AutoForm/useAutoForm';
import {ScrollableModal} from '../../../components/Common/ScrollableModal';
import validateUUID from '../../../utils/validateUUID';
import {UPDATES} from '../../../shared/UPDATES';

const classes = {
  fieldName: {
    width: '20%',
  },

  wrapper: {
    marginTop: '1rem',
  },
};

export const loadProtectionPlan = ({
  recommendDetails,
  setValues,
}: {
  recommendDetails: OvProtectionrecommendDetails_ovrecommendDetails | null;
  setValues: SetValuesWithSourceKey<recommendFieldsType>;
}) => {
  if (!recommendDetails) {
    return;
  }
  const loadedProtectionPlan = recommendDetails.pots?.[0].pot;
  const protectionPlan: setUpProtectionFields = {
    livesAssured: loadedProtectionPlan?.livesAssured || undefined,
    premiumType: loadedProtectionPlan?.premiumType || undefined,
    lifeCover: loadedProtectionPlan?.lifeCover
      ? String(loadedProtectionPlan?.lifeCover)
      : undefined,
    protectionType: loadedProtectionPlan?.protectionType || undefined,

    terminatingAge: loadedProtectionPlan?.terminatingAge || undefined,
    termType: loadedProtectionPlan?.termType || undefined,
    criticalIllnessCover: loadedProtectionPlan?.criticalIllnessCover
      ? String(loadedProtectionPlan?.criticalIllnessCover)
      : undefined,
    term: toString(loadedProtectionPlan?.term) || undefined,
    lifeOrCriticalIllnessCover: loadedProtectionPlan?.lifeOrCriticalIllnessCover
      ? String(loadedProtectionPlan?.lifeOrCriticalIllnessCover)
      : undefined,
    inTrust: loadedProtectionPlan?.inTrust || false,
    investmentLinked: loadedProtectionPlan?.investmentLinked || false,
    monthlyIncome: loadedProtectionPlan?.monthlyIncome
      ? String(loadedProtectionPlan?.monthlyIncome)
      : undefined,
    waiverOfPremium: loadedProtectionPlan?.waiverOfPremium || false,
    notes: loadedProtectionPlan?.notes || undefined,
    deferredPeriod: loadedProtectionPlan?.deferredPeriod || undefined,
  };
  setValues(UPDATES._520, (prev) =>
    merge({}, prev, {
      protectionPlan,
      loaded: true,
    })
  );
};

export const numberOnlyTerm = ({
  termObj,
  termHelpers,
}: {
  termObj: FieldInputProps<string | undefined>;
  termHelpers: AutoFieldHelpers<string | undefined>;
}) => {
  // term is only numbers
  const numbersOnly = String(termObj.value).replace(/[^\d.-]+/g, '');
  if (numbersOnly !== termObj.value && numbersOnly !== '') {
    termHelpers.setValue(UPDATES._521, numbersOnly);
  }
};

export const ProtectionPlan: React.FC<RecommendProps> = (props) => {
  const {contactName} = props;
  const [protectionPlanObj, protectionPlanMeta, protectionPlanHelpers] = useAutoField<
    setUpProtectionFields | undefined
  >('protectionPlan');
  const [termObj, termMeta, termHelpers] = useAutoField<
    setUpProtectionFields['term'] | undefined
  >(termField.id);
  const {ovRecommendId} = useRecommendContext();
  const {data, error, loading} = useQuery<
    OvProtectionrecommendDetails,
    OvProtectionrecommendDetailsVariables
  >(OV_PROTECTION_RECOMMENDATION_DETAILS, {
    skip: !ovRecommendId,
    variables: {
      ovrecommendId: validateUUID(ovRecommendId)!,
    },
  });
  const recommendDetails = useMemo(() => {
    return data?.ovrecommendDetails;
  }, [data?.ovrecommendDetails]);

  const {values, setValues} = useAutoForm<recommendFieldsType>();

  useEffect(() => {
    if (recommendDetails)
      loadProtectionPlan({
        recommendDetails,
        setValues,
      });
  }, [recommendDetails]);
  if (loading) {
    return <Loading entityName="Plans" />;
  }
  return (
    <Box width="100%" mt={3} mb={3}>
      <RecommendPlanForm
        sameReview={false}
        reccId=""
        id="notused"
        validation={{invalidFields: 0, totalFields: 1}}
        summary={
          <Typography>
            New <strong>personal </strong>protection plan for{' '}
            {values?.contact === OWNERSHIP.JOINT_CODE
              ? OWNERSHIP.JOINT_LIVES
              : contactName}{' '}
          </Typography>
        }
        details={
          <Box mt={2}>
            <Grid container direction="row" spacing={2}>
              <Box sx={classes.wrapper} mt={2} mb={2} mr={2} ml={2}>
                <Grid container direction="row" spacing={6}>
                  <Grid item xs={6}>
                    <Grid container direction="column" spacing={2}>
                      <Grid item>
                        <AutoField field={protectionTypeField} />
                      </Grid>
                      {values.protectionPlan?.protectionType ? (
                        <ProtectionForm {...props} />
                      ) : null}
                      <Box mt={1} />
                    </Grid>
                  </Grid>
                  {values.protectionPlan?.protectionType && (
                    <Grid item xs={6}>
                      <Grid container direction="column" spacing={2}>
                        <Grid item>
                          <AutoField field={protectionPlanNotesField} />
                        </Grid>
                        <Box mt={1} />
                      </Grid>
                    </Grid>
                  )}
                </Grid>
                <Grid item>
                  <Box mt={2} />
                  <Typography color="error">
                    <strong>
                      Details of the client&apos;s height, weight and smoker status will
                      need to have been obtained and added to their Client Questionnaire,
                      along with occupation when requiring income protection.
                    </strong>
                  </Typography>
                </Grid>
              </Box>
            </Grid>
          </Box>
        }
        isSelected
        isSubplan={false}
        handleChange={() => {}}
      />
    </Box>
  );
};

export const protectionOrder: (keyof protectionFields)[] = [
  'livesAssured',
  'premiumType',
  'lifeCover',
  'monthlyIncome',
  'criticalIllnessCover',
  'lifeOrCriticalIllnessCover',
  'deferredPeriod',
  'investmentLinked',
  'termType',
  'waiverOfPremium',
  'inTrust',
];

export const ProtectionForm: React.FC<RecommendProps> = ({contactName}) => {
  const {values} = useAutoForm<recommendFieldsType>();

  const isSingle =
    values.contact !== OWNERSHIP.JOINT_CODE && values.contact !== OWNERSHIP.JOINT_LIVES;
  const protectionType = values.protectionPlan!.protectionType!;
  const [showOtherProtectionModal, setShowOtherProtectionModal] = useState(false);
  useEffect(() => {
    if (
      values.protectionPlan?.protectionType === ProtectionTypeEnum.other // todo, change to other
    ) {
      setShowOtherProtectionModal(true);
    }
  }, [values.protectionPlan?.protectionType]);
  return (
    <>
      <ScrollableModal size="small" open={showOtherProtectionModal}>
        <Box bgcolor="white" padding={4}>
          <Typography variant="h4">Other Protection Type</Typography>
          <Box mt={2} />
          <Typography variant="body1">
            Please include the Protection Type and any other relevant information not
            covered by the drop down options in the notes section{' '}
          </Typography>
          <Box mt={4} />
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              setShowOtherProtectionModal(false);
            }}
          >
            Ok
          </Button>
        </Box>
      </ScrollableModal>
      {protectionTypeToFieldsMatrix.livesAssured[protectionType] && (
        <Grid item>
          <AutoField
            field={{
              ...livesAssuredField,

              initialValue: isSingle ? contactName : 'both',
              options: isSingle
                ? [{label: contactName, value: contactName}]
                : [
                    {label: 'Both', value: 'both'},
                    {label: 'Both beyond death', value: 'bothBeyondDeath'},
                  ],
            }}
          />
        </Grid>
      )}
      {protectionTypeToFieldsMatrix.premiumType[protectionType] && (
        <Grid item>
          <AutoField field={premiumTypeField} />
        </Grid>
      )}
      {protectionTypeToFieldsMatrix.lifeCover[protectionType] && (
        <Grid item>
          <AutoField field={liveCoverField} />
        </Grid>
      )}
      {protectionTypeToFieldsMatrix.monthlyIncome[protectionType] && (
        <Grid item>
          <AutoField field={monthlyIncomeTextField} />
        </Grid>
      )}
      {protectionTypeToFieldsMatrix.criticalIllnessCover[protectionType] && (
        <Grid item>
          <AutoField field={criticalIllnessCoverField} />
        </Grid>
      )}
      {protectionTypeToFieldsMatrix.deferredPeriod[protectionType] && (
        <Grid item>
          <AutoField field={deferredPeriodField} />
        </Grid>
      )}
      {protectionTypeToFieldsMatrix.investmentLinked[protectionType] && (
        <Grid item>
          <AutoField field={investmentLinkedField} />
        </Grid>
      )}
      {protectionTypeToFieldsMatrix.termType[protectionType] && (
        <Grid item>
          <AutoField field={termTypeField} />
        </Grid>
      )}
      {protectionTypeToFieldsMatrix.termType[protectionType] &&
        values.protectionPlan?.termType && (
          <Grid item>
            <AutoField
              field={
                values.protectionPlan?.termType === 'terminatingAge'
                  ? toAgeField
                  : termField
              }
            />
          </Grid>
        )}

      {protectionTypeToFieldsMatrix.waiverOfPremium[protectionType] && (
        <Grid item>
          <AutoField field={waiverOfPremiumField} />
        </Grid>
      )}
      {protectionTypeToFieldsMatrix.inTrust[protectionType] && (
        <Grid item>
          <AutoField field={protectionInTrustField} />
        </Grid>
      )}
    </>
  );
};
